import axios from 'axios';
import { sortOrder } from '../utils/mappers';

const DEFAULT_TIMEOUT = 30000;
const LONG_TIMEOUT = 30000;
const EXTRA_LONG_TIMEOUT = 60000;

class PautaClient {
  constructor(baseUrl) {
    this._httpClient = PautaClient.makeClient(baseUrl);
    this._httpClient.defaults.withCredentials = true;
  }

  static makeClient(baseUrl) {
    const client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
    });
    client.interceptors.request.use(function(config) {
      config.headers['x-newshub-trace'] =
        'ui-' +
        Math.random()
          .toString(36)
          .slice(2);

      return config;
    });
    return client;
  }

  loginOID(code) {
    return this._httpClient
      .post(
        '/auth/login/openid',
        {
          code: code,
          redirect_uri: process.env.VUE_APP_REDIRECT_URL,
        },
        {
          // setting a very long timeout as a workaround
          // because the ADFS service takes a very long time
          // to respond if the username do not exist.
          // if the timeout is less than 16 seconds, we
          // get a timeout error instead of a 401 unauthorized.
          timeout: LONG_TIMEOUT,
        },
      )
      .then(response => response.data);
  }

  loginCatraca({ username, password }) {
    return this._httpClient
      .post(
        '/auth/login/catraca',
        { username, password },
        {
          // setting a very long timeout as a workaround
          // because the ADFS service takes a very long time
          // to respond if the username do not exist.
          // if the timeout is less than 16 seconds, we
          // get a timeout error instead of a 401 unauthorized.
          timeout: LONG_TIMEOUT,
        },
      )
      .then(response => response.data);
  }

  getFolders(authToken) {
    return this._httpClient
      .get('/news/search', {
        timeout: EXTRA_LONG_TIMEOUT,
        headers: { Authorization: authToken },
      })
      .then(response => response.data);
  }

  search({
    token,
    pageSize,
    page,
    query = '',
    fromDate = null,
    toDate = null,
    author = '',
    folders = [],
    contact = false,
    order = sortOrder.DESC,
  }) {
    return this._httpClient
      .get('/news/search', {
        params: {
          page_size: pageSize,
          page,
          query,
          from_date: fromDate,
          to_date: toDate,
          author: author,
          folders: folders,
          display_contacts: contact,
          order,
        },
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getFolderContent(token, place, folder) {
    return this._httpClient
      .get(`/news/folder/${place}/${folder}`, {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getStory(token, id) {
    return this._httpClient
      .get(`/news/story/${id}`, {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getHistoricStory(token, id, contact = false) {
    return this._httpClient
      .get(`/news/storage/story/${id}`, {
        params: { display_contacts: contact },
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  searchConflicts(token, story_id, pageSize, page = 1) {
    return this._httpClient
      .get(`/news/search-conflicts/${story_id}`, {
        params: {
          page_size: pageSize,
          page,
        },
        timeout: DEFAULT_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  refreshFolders(authToken) {
    return this._httpClient.get('/news/refresh-folders', {
      timeout: EXTRA_LONG_TIMEOUT,
      headers: { Authorization: authToken },
    });
  }

  refreshUserProfiles(authToken) {
    return this._httpClient.get('/mirror/refresh-user-profiles', {
      timeout: EXTRA_LONG_TIMEOUT,
      headers: { Authorization: authToken },
    });
  }

  loadSystemFoldersTree(authToken, filter) {
    return this._httpClient.get('/system/folders', {
      params: {
        filter_path: filter.path,
      },
      timeout: EXTRA_LONG_TIMEOUT,
      headers: { Authorization: authToken },
    });
  }

  getFolderFields(authToken, place, folder) {
    return this._httpClient.get(`/news/fields/${place}/${folder}`, {
      timeout: EXTRA_LONG_TIMEOUT,
      headers: { Authorization: authToken },
    });
  }

  getSlugToEdit(authToken, story) {
    return this._httpClient
      .get(`/news/${story.story_uuid}/to_edit`, {
        params: { place: story.place, path: story.path },
        timeout: EXTRA_LONG_TIMEOUT,
        headers: { Authorization: authToken },
      })
      .then(response => {
        return response.data || {};
      });
  }

  reorderStories(token, stories, place) {
    return this._httpClient
      .put(`/news/reorder/${place}`, stories, {
        timeout: EXTRA_LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  updateStory(token, story) {
    return this._httpClient
      .put(`/news/update`, story, {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => {
        return response.data;
      });
  }

  deleteStory(token, story) {
    return this._httpClient
      .delete(`/news/${story.story_id}`, {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getRelatedVideos(token, title, body, manual_search, page_size, next_page) {
    return this._httpClient
      .post(
        '/video/search',
        {
          title: title,
          body: body,
          manual_search: manual_search,
        },
        {
          params: {
            page_size: page_size,
            page: next_page,
          },
          timeout: EXTRA_LONG_TIMEOUT,
          headers: { Authorization: token },
        },
      )
      .then(response => response.data);
  }
  openInewsConnection(token, place) {
    return this._httpClient.get(`/news/open-connection/${place}`, {
      timeout: LONG_TIMEOUT,
      headers: { Authorization: token },
    });
  }

  getWriteFolders(token) {
    return this._httpClient
      .get('/news/writing-folders', {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  createStory(token, story) {
    return this._httpClient
      .post('/news/create', story, {
        timeout: EXTRA_LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getUsersProgrammedActivities(token, username, cursor = 0) {
    return this._httpClient
      .get(`mirror/${username}/activities?cursor=${cursor}`, {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getPrograms(token) {
    return this._httpClient
      .get('mirror/info', {
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getMirrorByName(token, name, place) {
    return this._httpClient
      .get(`mirror/content/${name}`, {
        params: { place: place },
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getHistoricMirror(token, mirrorShortName, place, filterDate) {
    return this._httpClient
      .get(`mirror/storage/${mirrorShortName}`, {
        params: { place, filter_date: filterDate },
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getMirrorActivity(token, programName, place, id, field) {
    return this._httpClient
      .get(`mirror/detail/${programName}/${id}`, {
        params: { place: place, field: field },
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  checkForUpdateOnMirror(token, folder, place) {
    return this._httpClient
      .get(`mirror/check/change/${folder}`, {
        params: { place: place },
        timeout: LONG_TIMEOUT,
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getSystemFoldersMirrorHistoricTree(token) {
    return this._httpClient
      .get('/system/folders/mirror/historic', {
        headers: { Authorization: token },
      })
      .then(response => response.data);
  }

  getSystemHistoricMirror(
    token,
    { places_and_folders, from_date, to_date, after },
  ) {
    const { processing_date, place, path } = after || {};
    return this._httpClient
      .get(`/mirror/storage/processing-date/group`, {
        headers: { Authorization: token },
        params: {
          places_and_folders,
          from_date,
          to_date,
          after_processing_date: processing_date,
          after_place: place,
          after_path: path,
        },
      })
      .then(response => response.data);
  }

  getMirrorHistoricAndProcessingDateGroup(
    token,
    { places_and_folders, from_date, to_date },
  ) {
    return this._httpClient
      .get(`/mirror/storage/processing-date/group`, {
        headers: { Authorization: token },
        params: {
          places_and_folders,
          from_date,
          to_date,
        },
      })
      .then(response => response.data);
  }
}

export { PautaClient };
